<template>
  <b-modal
    ref="quickViewModal"
    :title="modalTitle"
    size="xl"
    hide-footer
    scrollable
    @hidden="onHidden"
  >
    <div v-if="onLoading" class="text-center py-5">
      <b-spinner variant="primary" label="Đang tải"></b-spinner>
    </div>

    <div v-else-if="errorMessage">
      <b-alert show variant="danger">{{ errorMessage }}</b-alert>
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" size="sm" class="mr-2" @click="openDetailPage">
          Mở trang chi tiết
        </b-button>
        <b-button variant="primary" size="sm" @click="fetchRequest">
          <i class="fas fa-redo"></i> Thử lại
        </b-button>
      </div>
    </div>

    <div v-else class="blr-quick-view">
      <!-- Thông tin chung -->
      <h5 class="blr-qv-title">Thông tin chung</h5>
      <div class="blr-qv-grid mb-4">
        <div class="blr-qv-item">
          <div class="blr-qv-label">ID phiếu</div>
          <div class="blr-qv-value"><strong>#{{ request.id }}</strong></div>
        </div>
        <div class="blr-qv-item">
          <div class="blr-qv-label">Trạng thái</div>
          <div class="blr-qv-value">
            <b-badge :variant="statusVariant(request.status)">{{ statusName(request.status) }}</b-badge>
          </div>
        </div>
        <div class="blr-qv-item">
          <div class="blr-qv-label">Doanh nghiệp</div>
          <div class="blr-qv-value">{{ request.companyName || '—' }}</div>
        </div>
        <div class="blr-qv-item">
          <div class="blr-qv-label">Phòng ban</div>
          <div class="blr-qv-value">{{ request.departmentName || request.departmentId || '—' }}</div>
        </div>
        <div class="blr-qv-item">
          <div class="blr-qv-label">Khoản mục</div>
          <div class="blr-qv-value">{{ request.costItemName || request.costItemId || '—' }}</div>
        </div>
        <div class="blr-qv-item">
          <div class="blr-qv-label">Cửa hàng</div>
          <div class="blr-qv-value">{{ request.storeName || 'Cấp phòng ban' }}</div>
        </div>
        <div class="blr-qv-item">
          <div class="blr-qv-label">Tổng số tiền</div>
          <div class="blr-qv-value">
            <strong class="text-primary">{{ formatAmount(request.totalAmount) }} đ</strong>
          </div>
        </div>
        <div class="blr-qv-item">
          <div class="blr-qv-label">Người tạo</div>
          <div class="blr-qv-value">{{ request.createdByName || request.createdBy || '—' }}</div>
        </div>
        <div class="blr-qv-item">
          <div class="blr-qv-label">Ngày tạo</div>
          <div class="blr-qv-value">{{ formatDateTime(request.createdAt) }}</div>
        </div>
      </div>

      <!-- Phân bổ theo kỳ (đầy đủ ngân sách hiện tại) -->
      <div v-if="periods.length" class="blr-qv-block">
        <div class="blr-qv-label mb-1">Phân bổ theo kỳ</div>
        <div class="blr-qv-table-scroll">
          <b-table-simple small bordered responsive class="mb-1">
            <b-thead>
              <b-tr>
                <b-th style="width:40px" class="text-center">#</b-th>
                <b-th style="width:110px">Kỳ</b-th>
                <b-th class="text-right">Số tiền yêu cầu (đ)</b-th>
                <b-th class="text-right">Ngân sách hiện tại (đ)</b-th>
                <b-th class="text-right">Đã sử dụng (đ)</b-th>
                <b-th class="text-right">Còn lại (đ)</b-th>
                <b-th style="width:130px" class="text-center">Trạng thái NS</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr v-for="(p, idx) in periods" :key="`period-${idx}`">
                <b-td class="text-center">{{ idx + 1 }}</b-td>
                <b-td>{{ p.month }}/{{ p.year }}</b-td>
                <b-td class="text-right">{{ formatAmount(p.amount) }}</b-td>
                <template v-if="p.currentBudget">
                  <b-td class="text-right">
                    <a
                      v-if="p.currentBudget.yearBudgetLineId"
                      :href="buildYearBudgetLineHref(p.currentBudget.yearBudgetLineId)"
                      target="_blank"
                      rel="noopener"
                    >
                      {{ formatAmount(p.currentBudget.allocatedAmount) }}
                      <i class="fas fa-external-link-alt fa-sm ml-1"></i>
                    </a>
                    <template v-else>{{ formatAmount(p.currentBudget.allocatedAmount) }}</template>
                  </b-td>
                  <b-td class="text-right">{{ formatAmount(p.currentBudget.usedAmount) }}</b-td>
                  <b-td class="text-right" :class="remainingClass(p.currentBudget.remainingAmount)">
                    {{ formatAmount(p.currentBudget.remainingAmount) }}
                  </b-td>
                  <b-td class="text-center">
                    <b-badge :variant="budgetStatusVariant(p.currentBudget.status)">
                      {{ budgetStatusName(p.currentBudget.status) }}
                    </b-badge>
                  </b-td>
                </template>
                <template v-else>
                  <b-td class="text-right text-muted">—</b-td>
                  <b-td class="text-right text-muted">—</b-td>
                  <b-td class="text-right text-muted">—</b-td>
                  <b-td class="text-center"><b-badge variant="secondary">Chưa có</b-badge></b-td>
                </template>
              </b-tr>
            </b-tbody>
            <b-tfoot>
              <b-tr>
                <b-td colspan="2" class="text-right"><strong>Tổng cộng</strong></b-td>
                <b-td class="text-right">
                  <strong class="text-primary">{{ formatAmount(request.totalAmount) }}</strong>
                </b-td>
                <b-td class="text-right"><strong>{{ formatAmount(periodsTotals.allocated) }}</strong></b-td>
                <b-td class="text-right"><strong>{{ formatAmount(periodsTotals.used) }}</strong></b-td>
                <b-td class="text-right" :class="remainingClass(periodsTotals.remaining)">
                  <strong>{{ formatAmount(periodsTotals.remaining) }}</strong>
                </b-td>
                <b-td></b-td>
              </b-tr>
            </b-tfoot>
          </b-table-simple>
        </div>
      </div>

      <!-- Lý do yêu cầu -->
      <div class="blr-qv-block">
        <div class="blr-qv-label mb-1">Lý do yêu cầu</div>
        <div class="blr-qv-reason">{{ request.reason || '—' }}</div>
      </div>

      <!-- Link ngân sách nếu đã duyệt -->
      <div
        v-if="request.status === BUDGET_LINE_REQUEST_STATUS.APPROVED && request.budgetLineId"
        class="blr-qv-block"
      >
        <b-alert variant="success" show class="mb-0">
          Yêu cầu đã được duyệt.
          <router-link
            :to="{ name: 'detail-budget-line', query: { id: request.budgetLineId } }"
            class="alert-link ml-2"
          >
            Xem ngân sách
          </router-link>
        </b-alert>
      </div>

      <!-- Tệp đính kèm -->
      <div class="blr-qv-block">
        <div class="blr-qv-label mb-1">Tệp đính kèm</div>
        <FileUploadManager
          v-if="request.id"
          :entity-id="request.id"
          :entity="UPLOAD_ENTITY.BUDGET_LINE_REQUEST"
          :disabled="true"
        />
      </div>

      <!-- Tóm tắt duyệt -->
      <div class="blr-qv-block">
        <PurchaseRequestApprovalSummary :approvers="request.approvers || []" />
      </div>

      <div class="d-flex justify-content-end mt-3">
        <b-button variant="primary" size="sm" @click="openDetailPage">
          Mở trang chi tiết đầy đủ
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import ApiService from '@/core/services/api.service';
import { convertPrice } from '@/utils/common';
import { formatDate, CLIENT_DATE_TIME_NO_SECONDS } from '@/utils/date';
import {
  BUDGET_LINE_REQUEST_STATUS,
  BUDGET_LINE_REQUEST_STATUS_BADGE_VARIANT,
  BUDGET_STATUS_DETAIL_BADGE_VARIANT,
  UPLOAD_ENTITY,
} from '@/utils/enum';
import { BUDGET_LINE_REQUEST_STATUS_NAME, BUDGET_STATUS_DETAIL_NAME } from '@/utils/enum-name';
import PurchaseRequestApprovalSummary from '../../purchase-requests/components/PurchaseRequestApprovalSummary.vue';
import FileUploadManager from '@/view/modules/upload/FileUploadManager.vue';

export default {
  name: 'BudgetLineRequestQuickViewModal',
  components: {
    PurchaseRequestApprovalSummary,
    FileUploadManager,
  },
  data() {
    return {
      BUDGET_LINE_REQUEST_STATUS,
      UPLOAD_ENTITY,
      requestId: null,
      request: {},
      onLoading: false,
      errorMessage: '',
      // Tăng mỗi lần mở/đóng để bỏ qua response của phiếu đã đóng hoặc đã chuyển
      requestSeq: 0,
    };
  },
  computed: {
    modalTitle() {
      return this.request.id
        ? `Chi tiết phiếu bổ sung ngân sách #${this.request.id}`
        : 'Chi tiết phiếu bổ sung ngân sách';
    },
    periods() {
      return Array.isArray(this.request.periods) ? this.request.periods : [];
    },
    periodsTotals() {
      return this.periods.reduce(
        (acc, p) => {
          const cb = p && p.currentBudget;
          acc.allocated += cb ? Number(cb.allocatedAmount) || 0 : 0;
          acc.used += cb ? Number(cb.usedAmount) || 0 : 0;
          acc.remaining += cb ? Number(cb.remainingAmount) || 0 : 0;
          return acc;
        },
        { allocated: 0, used: 0, remaining: 0 },
      );
    },
  },
  methods: {
    open(id) {
      this.requestId = id;
      this.request = {};
      this.errorMessage = '';
      this.$refs.quickViewModal.show();
      this.fetchRequest();
    },
    onHidden() {
      this.requestSeq += 1;
      this.onLoading = false;
    },
    fetchRequest() {
      if (!this.requestId) return;
      this.requestSeq += 1;
      const seq = this.requestSeq;
      this.onLoading = true;
      this.errorMessage = '';
      ApiService.get(`v2/budget-line-requests/${this.requestId}`)
        .then(({ data }) => {
          if (seq !== this.requestSeq) return;
          if (data.status === 200) {
            this.request = data.data || {};
          } else {
            this.errorMessage = data.message || 'Không tải được chi tiết phiếu.';
          }
          this.onLoading = false;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          this.errorMessage =
            error.response?.data?.message ??
            error.message ??
            'Không tải được chi tiết phiếu.';
          this.onLoading = false;
        });
    },
    openDetailPage() {
      const id = this.requestId;
      this.$refs.quickViewModal.hide();
      this.$router.push({
        name: 'detail-budget-line-request',
        query: { id },
      });
    },
    formatDateTime(val) {
      return val ? formatDate(val, CLIENT_DATE_TIME_NO_SECONDS) : '';
    },
    formatAmount(val) {
      return convertPrice(Number(val) || 0, 'vi-VN');
    },
    statusVariant(val) {
      return BUDGET_LINE_REQUEST_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    statusName(val) {
      return BUDGET_LINE_REQUEST_STATUS_NAME[val] || val;
    },
    budgetStatusName(status) {
      if (status == null) return 'Chưa có';
      return BUDGET_STATUS_DETAIL_NAME[status] || '—';
    },
    budgetStatusVariant(status) {
      if (status == null) return 'secondary';
      return BUDGET_STATUS_DETAIL_BADGE_VARIANT[status] || 'secondary';
    },
    remainingClass(val) {
      const n = Number(val) || 0;
      if (n > 0) return 'text-success';
      if (n < 0) return 'text-danger';
      return '';
    },
    buildYearBudgetLineHref(yearBudgetLineId) {
      if (!yearBudgetLineId) return '#';
      const route = this.$router.resolve({
        name: 'detail-budget-year-group',
        query: { id: yearBudgetLineId },
      });
      return (route && route.href) || `/budget/budget-lines/year-detail?id=${yearBudgetLineId}`;
    },
  },
};
</script>

<style scoped>
.blr-quick-view {
  font-size: 14px;
  color: #3f4254;
}
.blr-qv-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 12px;
}
.blr-qv-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  column-gap: 24px;
  row-gap: 14px;
}
.blr-qv-item {
  min-width: 0;
}
.blr-qv-label {
  font-weight: 500;
  font-size: 13px;
  color: #8c8ea3;
}
.blr-qv-value {
  margin-top: 2px;
  font-size: 14px;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.blr-qv-block {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f1f5;
}
.blr-qv-table-scroll {
  overflow-x: auto;
}
.blr-qv-reason {
  width: 100%;
  padding: 8px 12px;
  background-color: #f7f8fa;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
  max-height: 160px;
  overflow-y: auto;
  overscroll-behavior: contain;
}
</style>
